import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type Theme = "light" | "dark";

type ThemeContextValue = {
  theme: Theme;
  toggleTheme: () => void;
};

type ThemeState = {
  theme: Theme;
  systemTheme: Theme;
  isSystemPreferred: boolean;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_COOKIE = "theme";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 año

const isBrowser = typeof window !== "undefined";

const getRootElement = () => (typeof document === "undefined" ? null : document.documentElement);

const applyThemeClass = (theme: Theme) => {
  const root = getRootElement();
  if (!root) return;

  root.classList.toggle("dark", theme === "dark");
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
};

const readThemeCookie = (): Theme | null => {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(new RegExp(`(?:^|; )${THEME_COOKIE}=([^;]*)`));
  if (!match) return null;

  const value = decodeURIComponent(match[1]);
  return value === "light" || value === "dark" ? value : null;
};

const writeThemeCookie = (value: Theme | null) => {
  if (typeof document === "undefined") return;

  if (!value) {
    document.cookie = `${THEME_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
    return;
  }

  document.cookie = `${THEME_COOKIE}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
};

const getSystemTheme = (): Theme => {
  if (!isBrowser) return "light";
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const resolveInitialTheme = (): ThemeState => {
  if (!isBrowser) {
    return { theme: "light", isSystemPreferred: true, systemTheme: "light" };
  }

  const stored = readThemeCookie();
  const systemTheme = getSystemTheme();

  if (stored) {
    applyThemeClass(stored);
    return { theme: stored, isSystemPreferred: false, systemTheme };
  }

  applyThemeClass(systemTheme);
  return { theme: systemTheme, isSystemPreferred: true, systemTheme };
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [{ theme, isSystemPreferred }, setThemeState] = useState<ThemeState>(resolveInitialTheme);

  useEffect(() => {
    if (!isBrowser) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event: MediaQueryListEvent) => {
      const nextSystemTheme: Theme = event.matches ? "dark" : "light";
      setThemeState((prev) => {
        if (prev.isSystemPreferred) {
          applyThemeClass(nextSystemTheme);
          return { theme: nextSystemTheme, isSystemPreferred: true, systemTheme: nextSystemTheme };
        }

        return { ...prev, systemTheme: nextSystemTheme };
      });
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener?.(handleChange);
    return () => mediaQuery.removeListener?.(handleChange);
  }, []);

  useEffect(() => {
    if (!isBrowser) return;

    applyThemeClass(theme);
    writeThemeCookie(isSystemPreferred ? null : theme);
  }, [theme, isSystemPreferred]);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => ({
      theme: prev.theme === "dark" ? "light" : "dark",
      isSystemPreferred: false,
      systemTheme: prev.systemTheme,
    }));
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
